'use client';

import NextLink from 'next/link';
import Image from 'next/image';

import logo from '@/public/images/logo.png';
import { menuItems } from '@/src/defaults/menuDefaults';
import phoneIcon from '@/public/images/pages/phone.svg';
import emailIcon from '@/public/images/pages/email.svg';
import arco from '@/public/images/pages/arco.png';
import { useSiteSettings } from '@/src/context/SiteSettingsContext';

import CornerDownRightIcon from '../UI/CornerDownRightIcon';
import Link from '../UI/Link';
import Paragraph from '../Typography/Paragraph';

const Footer = () => {
  const contactStyles = 'flex gap-4 flex-wrap align-center mb-6 last:mb-0';
  const settings = useSiteSettings();
  const telHref = `tel:${settings.phone.replace(/[^0-9+]/g, '')}`;

  return (
    <footer className="pt-24 px-8 bg-blue border-t-2 border-t-black">
      <div className="max-w-theme mx-auto">
        <div className="inline-grid md:grid md:gap-8 md:grid-cols-6">
          {menuItems.map((item, index) => (
            <nav className="mt-6 lg:mt-0" key={index}>
              {item?.href ? (
                <Link
                  href={item.href}
                  style="text-base hover:underline mb-6 block"
                  title={item.title}
                />
              ) : (
                <span className="block text-black text-base mb-6">
                  {item.title}
                </span>
              )}
              {'subMenu' in item && item.subMenu && (
                <ul>
                  {item.subMenu.map((subItem, subIndex) => (
                    <li className="mb-6 last:mb-0" key={subIndex}>
                      <Link
                        href={subItem.href}
                        style="text-base hover:underline"
                        title={subItem.title}
                        ariaLabel={
                          subItem.title === 'Events'
                            ? `${item.title} events`
                            : undefined
                        }
                      />
                      {subItem.subMenu && (
                        <ul className="mt-6">
                          {subItem.subMenu.map((nestedItem, nestedIndex) => (
                            <li className="mb-6 last:mb-0" key={nestedIndex}>
                              <Link
                                href={nestedItem.href}
                                style="text-base hover:underline inline-flex items-center gap-2"
                                title={nestedItem.title}
                                target={nestedItem.target}
                                ariaLabel={`${subItem.title} ${nestedItem.title.toLowerCase()}`}
                                leadingIcon={
                                  <CornerDownRightIcon className="h-5 w-5 shrink-0" />
                                }
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </nav>
          ))}
          <nav>
            <NextLink aria-label="Home page" href="/" title="Home page">
              <Image
                priority
                className="no-repeat max-w-full"
                alt="Passenger Logo"
                title="Passenger Logo"
                src={logo}
                height={29}
                width={128}
              />
            </NextLink>
            <ul className="mt-6">
              <li
                key="phone"
                className="flex gap-4 flex-wrap align-center mb-6"
              >
                <Image
                  alt="Passenger phone number"
                  src={phoneIcon}
                  height={24}
                  width={24}
                />
                <Link
                  href={telHref}
                  style="text-base hover:underline"
                  title="Call us"
                />
              </li>
              <li key="email" className={contactStyles}>
                <Image
                  alt="Passenger email"
                  src={emailIcon}
                  height={24}
                  width={24}
                />
                <Link
                  href={`mailto:${settings.email}`}
                  style="text-base hover:underline"
                  title="Email us"
                />
              </li>
              {settings.socialLinks.map(
                (social: { platform: string; url: string }, i: number) => (
                  <li key={social.platform || i} className={contactStyles}>
                    <Link
                      href={social.url}
                      style="text-base hover:underline"
                      target="_blank"
                      title={social.platform}
                    />
                  </li>
                )
              )}
            </ul>
            <div className="mt-6">
              <Image
                className="max-w-full"
                height={48}
                width={128}
                alt="Passenger Recovery Arco"
                src={arco}
              />
            </div>
          </nav>
        </div>
        <div className="py-8 text-center">
          <Paragraph
            style="!text-sm"
            text={`Copyright (c) ${new Date().getFullYear()} Passenger Recovery All Rights Reserved.`}
          />
          <Paragraph style="!text-sm" text={settings.copyright} />
          <Paragraph
            style="!text-sm"
            text="Site and Compass designed and developed by Matt Tompkins."
          />
        </div>
      </div>
    </footer>
  );
};
export default Footer;
